import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";

/** The iOS or Android app, as opposed to a browser or the installed web app. */
export const isNative = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform() as "ios" | "android" | "web";

/** The server the bundled app talks to; empty on the web, where the app and API share an origin. */
export const API_ORIGIN: string = import.meta.env.VITE_API_ORIGIN ?? "";

/** The bundled app's pages live on capacitor://localhost, so new-tab and off-site links open in the system browser. */
function openLinksInBrowser() {
  document.addEventListener("click", (e) => {
    const link = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!link) return;
    const href = link.getAttribute("href")!;
    const external = /^https?:/i.test(href);
    if (!external && link.target !== "_blank") return;
    e.preventDefault();
    void Browser.open({ url: external ? href : `${API_ORIGIN}${href}` });
  });
}

/** Set up the native shell. `onResume` runs when the app comes back to the foreground. */
export function initNative(onResume: () => void) {
  if (!isNative()) return;
  openLinksInBrowser();
  void CapApp.addListener("resume", onResume);
  void CapApp.addListener("backButton", ({ canGoBack }) => {
    if (canGoBack) window.history.back();
    else void CapApp.exitApp();
  });
  const syncStatusBar = (dark: boolean) => void StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {});
  const scheme = window.matchMedia("(prefers-color-scheme: dark)");
  syncStatusBar(scheme.matches);
  scheme.addEventListener("change", (e) => syncStatusBar(e.matches));
  void SplashScreen.hide();
}
